import { pageToQuery } from './pageIntoQueryParams';

function getFilterOperator(value: string, key: string): string {
  if (key === 'driverId') {
    if (value === 'unassigned') {
      return '$eq';
    }
    if (value === 'assigned') {
      return '$ne';
    }
  }
  if (key === 'status') {
    return '$eq';
  }
  if (key === 'cameraId') {
    return '$eq';
  }
  if (key === 'isValid') {
    return '$eq';
  }
  if (key === 'alertId') {
    if (value === 'alert') {
      return '$ne';
    }
    if (value === 'manual') {
      return '$eq';
    }
  }
  if (key === 'thumbnailFrontCamera' || key === 'thumbnailInwardCamera') {
    if (value === 'withVideo') {
      return '$ne';
    }
    if (value === 'withoutVideo') {
      return '$eq';
    }
  }
  if (key === 'startDate') {
    return '$gt';
  }
  if (key === 'endDate') {
    return '$lt';
  }
  if (key.toLowerCase().includes('id')) {
    return '$exists';
  }
  if (['true', 'false'].includes(value)) {
    return '$eq';
  }
  return '$regex';
}

function getFilterValue(value: Record<string, any>): string {
  if (value[0] === 'unassigned' || value[0] === 'assigned' || value[0] === 'alert' || value[0] === 'manual' || value[0] === 'withVideo' || value[0] === 'withoutVideo') {
    return ['null'].join('|');
  }

  return value?.join('|');
}

function createPageFilter(page?: number, customPageLimit?: number): string {
  const limit = customPageLimit ?? 500;
  if (page) {
    const { limit: pageLimit, skip } = pageToQuery(page, customPageLimit);
    return `?limit=${pageLimit}&skip=${skip}`;
  }
  return `?limit=${limit}&skip=0`;
}

function createSortFilter(filters: Record<string, any>): string {
  if (filters.sortBy) {
    const [, sortField, sortOrder] = /([a-zA-Z.]+)_([a-zA-Z]+)/.exec(filters.sortBy[0]) as string[];
    return `&sortField=${sortField}&sortOrder=${sortOrder}`;
  }
  return '';
}

export const getFilterFields = (filters: Record<string, any>, omitKeys: string[], dateField = 'createdAt', startIndex = 0): string => {
  let query = '';
  let index = startIndex;
  Object.keys(filters).forEach((key) => {
    if (!omitKeys.includes(key) && filters[key]?.length && filters[key]?.[0] !== 'null') {
      query += (key === 'startDate' || key === 'endDate') ? `&filter[${index}][field]=${dateField}`
        : `&filter[${index}][field]=${key}`;
      query += `&filter[${index}][operator]=${getFilterOperator(String(filters[key]?.[0]), key)}`;
      query += `&filter[${index}][value]=${getFilterValue(filters[key])}`;
      index += 1;
    }
  });

  return query;
};

export const queryFilterByGroups = (filters?: Record<string, any>): string => {
  let query = '';
  filters?.groupIds?.forEach((g: string, i: string) => {
    query += `&groupIds[${i}]=${g}`;
  });
  return query;
};

export const buildQueryPageParam = (
  baseQuery: string | undefined,
  skip: number,
  limit: number,
): string => {
  const query = baseQuery || '?limit=20&skip=0';

  return query
    .replace(/([?&])skip=\d+/, `$1skip=${skip}`)
    .replace(/([?&])limit=\d+/, `$1limit=${limit}`);
};

export const withShowStandaloneAndMergedTrips = (
  query: string,
  showStandaloneAndMergedTrips: boolean,
): string => {
  const value = showStandaloneAndMergedTrips ? 'true' : 'false';
  if (/([?&])showStandaloneAndMergedTrips=/.test(query)) {
    return query.replace(
      /([?&])showStandaloneAndMergedTrips=(true|false)/,
      `$1showStandaloneAndMergedTrips=${value}`,
    );
  }
  return `${query}${query.includes('?') ? '&' : '?'}showStandaloneAndMergedTrips=${value}`;
};

export const queryFilters = (filters: Record<string, any>, page?: number, customQuery?: string, dateField?: string, customPageLimit?: number): string => {
  let query = '';
  query += createPageFilter(page, customPageLimit);

  if (customQuery) {
    query += customQuery;
  }

  const omitKeys = ['sortBy', 'input', 'period', 'groupIds', 'isMergedTrip', 'showStandaloneAndMergedTrips'];
  query += getFilterFields(filters, omitKeys, dateField);
  query += createSortFilter(filters);

  if (filters.input) {
    query += `&input=${filters.input}`;
  }

  if (filters.groupIds) {
    query += queryFilterByGroups(filters);
  }

  if (filters.isMergedTrip?.[0] === 'true') {
    query += '&onlyMergedTrips=true';
  }

  if (filters.showStandaloneAndMergedTrips?.[0] === 'true'
    || filters.showStandaloneAndMergedTrips?.[0] === 'false') {
    query = withShowStandaloneAndMergedTrips(
      query,
      filters.showStandaloneAndMergedTrips[0] === 'true',
    );
  }

  return query;
};

export const getSimpleEqQuery = (key: string, value: string | undefined, i: number): string => {
  let query = '';
  query += `&filter[${i}][field]=${key}`;
  query += `&filter[${i}][operator]=$eq`;
  query += `&filter[${i}][value]=${value}`;

  return key && value && (i !== undefined && i !== null) ? query : '';
};

export const withInclusiveDateStart = (query: string, dateField = 'eventDate'): string => (
  query.replace(
    new RegExp(`(\\[field]=${dateField}&filter\\[\\d+]\\[operator]=\\$)gt`, 'g'),
    '$1gte',
  )
);

export const getSimpleNotEqQuery = (key: string, value: string | number, i: number): string => {
  let query = '';
  query += `&filter[${i}][field]=${key}`;
  query += `&filter[${i}][operator]=$ne`;
  query += `&filter[${i}][value]=${value}`;

  return key && (value !== undefined) && (i !== undefined && i !== null) ? query : '';
};

export const getSimpleGreaterQuery = (key: string, value: number, i: number): string => {
  let query = '';
  query += `&filter[${i}][field]=${key}`;
  query += `&filter[${i}][operator]=$gt`;
  query += `&filter[${i}][value]=${value}`;

  return key && (value !== undefined) && (i !== undefined && i !== null) ? query : '';
};

type PagedResponse<T> = { results: T[]; total?: number };
type InfiniteDataLike<P> = { pages: P[]; pageParams: unknown[] };

// Splices freshly-fetched "page 1" items into an already-loaded infinite query's first page,
// without touching pages 2..N. Used to auto-refresh infinite-scroll lists (alerts, trips, etc.)
// without re-fetching every loaded page or discarding scroll history the user already has open.
// Items already present anywhere in the loaded pages (matched by _id) are updated in place -
// e.g. a video that was "pending" when added still needs its thumbnail/status refreshed once
// processing finishes, even though it isn't a "new" item anymore.
export function mergeNewestIntoFirstPage<T extends { _id: string }, P extends PagedResponse<T>>(
  existing: InfiniteDataLike<P> | undefined,
  latestFirstPage: P | undefined,
): InfiniteDataLike<P> | undefined {
  if (!existing?.pages?.length || !latestFirstPage) {
    return existing;
  }

  const latestById = new Map(latestFirstPage.results.map((item) => [item._id, item]));
  let hasChanges = false;

  const updatedPages = existing.pages.map((page) => ({
    ...page,
    results: page.results.map((item) => {
      const latestItem = latestById.get(item._id);
      if (latestItem && latestItem !== item) {
        hasChanges = true;
        return latestItem;
      }
      return item;
    }),
  }));

  const existingIds = new Set(existing.pages.flatMap((page) => page.results.map((item) => item._id)));
  const newItems = latestFirstPage.results.filter((item) => !existingIds.has(item._id));

  if (!newItems.length && !hasChanges) {
    return existing;
  }

  const [firstPage, ...restPages] = updatedPages;

  return {
    ...existing,
    pages: [
      {
        ...firstPage,
        results: [...newItems, ...firstPage.results],
        total: latestFirstPage.total ?? firstPage.total,
      },
      ...restPages,
    ],
  };
}
